import {
  buildDeliverableLineDescription,
  customServiceUnitPrice,
  normalizePropertyAddress,
  type DeliverableBillingInput,
  type DeliverableBillingKind,
} from "@/modules/tickets/supplementPricing";
import {
  applyTemplateEdit,
  type MessageTemplateEdit,
} from "../../../supabase/functions/_shared/messageTemplates.ts";

const PAYMENT_CTA =
  "Please pay using the secure link below. After payment you will receive one email with your files attached.";

const SMS_SERVICE_SUBJECT: Record<DeliverableBillingKind, string> = {
  supplement: "Xactimate supplement",
  siding: "siding measurement",
  roof: "roof measurement",
  esx: "ESX file",
  pdf_analysis: "PDF analysis",
};

const SINGLE_ITEM_COPY: Record<
  DeliverableBillingKind,
  { subjectLabel: string; readyMessage: string; deliverySubject: string }
> = {
  supplement: {
    subjectLabel: "Invoice for supplement",
    readyMessage: "Your Xactimate supplement is ready.",
    deliverySubject: "Your supplement files are ready",
  },
  siding: {
    subjectLabel: "Invoice for siding measurement",
    readyMessage: "Your siding measurement is ready.",
    deliverySubject: "Your siding measurement files are ready",
  },
  roof: {
    subjectLabel: "Invoice for roof measurement",
    readyMessage: "Your roof measurement is ready.",
    deliverySubject: "Your roof measurement files are ready",
  },
  esx: {
    subjectLabel: "Invoice for ESX creation",
    readyMessage: "Your ESX file is ready.",
    deliverySubject: "Your ESX files are ready",
  },
  pdf_analysis: {
    subjectLabel: "Invoice for PDF analysis",
    readyMessage: "Your PDF analysis is ready.",
    deliverySubject: "Your PDF analysis files are ready",
  },
};

export type TicketPaymentCopy = {
  subject: string;
  message: string;
  deliverySubject: string;
  serviceLines: string[];
};

const billedDeliverables = (deliverables: DeliverableBillingInput[]) =>
  deliverables.filter(
    (item) => item.billing_kind || customServiceUnitPrice(item) != null,
  );

const serviceLineLabel = (item: DeliverableBillingInput, address: string) => {
  if (customServiceUnitPrice(item) != null) {
    return `${item.title?.trim() || "Service"} for ${address}`;
  }
  return buildDeliverableLineDescription(
    item.billing_kind as DeliverableBillingKind,
    address,
    item.billing_line_count,
  );
};

const humanizeBillingKind = (kind: string) =>
  kind.replace(/_/g, " ").replace(/\s+/g, " ").trim() || "services";

const resolveSingleItemCopy = (kind: DeliverableBillingKind) => {
  const known = SINGLE_ITEM_COPY[kind as keyof typeof SINGLE_ITEM_COPY];
  if (known) return known;

  const label = humanizeBillingKind(String(kind));
  return {
    subjectLabel: `Invoice for ${label}`,
    readyMessage: `Your ${label} is ready.`,
    deliverySubject: `Your ${label} files are ready`,
  };
};

export const buildTicketPaymentCopyFromDeliverables = <
  T extends DeliverableBillingInput,
>(
  deliverables: T[],
  /** Label for the subject (one address, or "N properties"). */
  propertyAddress: string,
  /** Combined invoices: each service names its own ticket's address. */
  addressFor?: (item: T) => string,
): TicketPaymentCopy => {
  const address = normalizePropertyAddress(propertyAddress);
  const billed = billedDeliverables(deliverables) as T[];
  const serviceLines = billed.map((item) =>
    serviceLineLabel(
      item,
      addressFor ? normalizePropertyAddress(addressFor(item)) : address,
    ),
  );

  if (billed.length === 1 && customServiceUnitPrice(billed[0]) != null) {
    const name = billed[0].title?.trim() || "Service";
    return {
      subject: `Invoice for ${name} (${address})`,
      message: `Your ${name} is ready.\n\n${PAYMENT_CTA}`,
      deliverySubject: `Your ${name} is ready (${address})`,
      serviceLines,
    };
  }

  if (billed.length === 1) {
    const kind = billed[0].billing_kind as DeliverableBillingKind;
    const copy = resolveSingleItemCopy(kind);
    return {
      subject: `${copy.subjectLabel} (${address})`,
      message: `${copy.readyMessage}\n\n${PAYMENT_CTA}`,
      deliverySubject: `${copy.deliverySubject} (${address})`,
      serviceLines,
    };
  }

  if (billed.length > 1) {
    return {
      subject: `Invoice for services (${address})`,
      message: `Your project deliverables are ready.\n\n${PAYMENT_CTA}`,
      deliverySubject: `Your project files are ready (${address})`,
      serviceLines,
    };
  }

  return {
    subject: `Invoice for services (${address})`,
    message: `Your project deliverables are ready.\n\n${PAYMENT_CTA}`,
    deliverySubject: `Your project files are ready (${address})`,
    serviceLines: [],
  };
};

export const resolveTicketSmsServiceSubject = (
  deliverables: DeliverableBillingInput[],
  propertyAddress?: string | null,
) => {
  const billed = billedDeliverables(deliverables);
  if (billed.length === 1 && customServiceUnitPrice(billed[0]) != null) {
    return (billed[0].title?.trim() || "service").toLowerCase();
  }
  if (billed.length === 1) {
    const kind = billed[0].billing_kind as DeliverableBillingKind;
    return (
      SMS_SERVICE_SUBJECT[kind as keyof typeof SMS_SERVICE_SUBJECT] ??
      humanizeBillingKind(String(kind))
    );
  }
  if (billed.length > 1) return "project deliverables";
  const address = propertyAddress?.trim();
  return address || "project";
};

export const buildTicketPaymentSmsText = (params: {
  orgName: string;
  paymentUrl: string;
  recipientFirstName?: string | null;
  serviceSubject: string;
}) => {
  const greeting = params.recipientFirstName?.trim()
    ? `Hi ${params.recipientFirstName.trim()},`
    : "Hi,";
  return `${greeting} your ${params.serviceSubject} is completed. Pay here ${params.paymentUrl} to receive your files. ${params.orgName}.`;
};

export const buildTicketPaymentReminderSmsText = (params: {
  orgName: string;
  paymentUrl: string;
  recipientFirstName?: string | null;
  serviceSubject: string;
}) => {
  const greeting = params.recipientFirstName?.trim()
    ? `Hi ${params.recipientFirstName.trim()},`
    : "Hi,";
  return `${greeting} your ${params.serviceSubject} invoice is still unpaid. Pay here ${params.paymentUrl} to receive your files. ${params.orgName}.`;
};

export const buildTicketPaymentReminderCopyFromDeliverables = (
  deliverables: DeliverableBillingInput[],
  propertyAddress: string,
): TicketPaymentCopy => {
  const base = buildTicketPaymentCopyFromDeliverables(
    deliverables,
    propertyAddress,
  );
  const reminderIntro =
    base.serviceLines.length === 1
      ? "This is a friendly reminder that your invoice is still unpaid."
      : "This is a friendly reminder that your services invoice is still unpaid.";

  return {
    ...base,
    subject: `Reminder: ${base.subject}`,
    message: `${reminderIntro}\n\n${PAYMENT_CTA}`,
  };
};

/**
 * The company's edit of a ticket email (Settings → Tickets → Client emails)
 * applied to the copy built per service; unedited fields keep that copy.
 */
export const applyTicketEmailTemplate = (
  copy: TicketPaymentCopy,
  key: "tickets.invoice.email" | "tickets.reminder.email",
  edit: MessageTemplateEdit | null | undefined,
  context: {
    propertyAddress: string;
    companyName?: string | null;
    clientFirstName?: string | null;
  },
): TicketPaymentCopy => {
  const { subject, body } = applyTemplateEdit(
    key,
    edit,
    {
      client_first_name: context.clientFirstName,
      company_name: context.companyName,
      property_address: context.propertyAddress,
      invoice_title: copy.subject
        .replace(/^Reminder:\s*/i, "")
        .replace(/\s*\([^)]*\)\s*$/, ""),
      ready_line: copy.message.split(/\n{2,}/)[0],
    },
    { subject: copy.subject, body: copy.message },
  );
  return { ...copy, subject, message: body };
};
